import { DefaultOptions, QueryClient } from '@tanstack/react-query';

import { ApiError } from './api-client';

// Type guard to check if error is an ApiError
function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'message' in error &&
    typeof (error as ApiError).status === 'number'
  );
}

// Default options for all queries and mutations
const defaultOptions: DefaultOptions = {
  queries: {
    // Stale time: how long data is considered fresh (5 minutes)
    staleTime: 1000 * 60 * 5,
    // Cache time: how long data stays in cache when unused (10 minutes)
    gcTime: 1000 * 60 * 10,
    // Retry failed requests 3 times with exponential backoff
    retry: (failureCount, error) => {
      // If it's an ApiError, check the status
      if (isApiError(error)) {
        // Don't retry on client errors (4xx) except 408 (timeout)
        if (error.status >= 400 && error.status < 500 && error.status !== 408) {
          return false;
        }
      }

      // Retry up to 3 times for server errors and network errors
      return failureCount < 3;
    },
    // Retry delay with exponential backoff
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Refetch on window focus in production
    refetchOnWindowFocus: process.env.NODE_ENV === 'production',
    // Don't refetch on reconnect by default
    refetchOnReconnect: false,
  },
  mutations: {
    // Retry mutations once for network errors
    retry: (failureCount, error) => {
      // If it's an ApiError, check the status
      if (isApiError(error)) {
        // Only retry network errors and 5xx server errors
        if (error.status === 0 || (error.status >= 500 && error.status < 600)) {
          return failureCount < 1;
        }
      }

      // For non-ApiError errors, don't retry
      return false;
    },
    // Retry delay for mutations
    retryDelay: 1000,
  },
};

// Create and configure the query client
export const queryClient = new QueryClient({
  defaultOptions,
});

// Query key factory for consistent key generation
export const queryKeys = {
  // Auth related queries
  auth: {
    all: ['auth'] as const,
    profile: () => [...queryKeys.auth.all, 'profile'] as const,
    permissions: () => [...queryKeys.auth.all, 'permissions'] as const,
  },

  // Firms related queries
  firms: {
    all: ['firms'] as const,
    lists: () => [...queryKeys.firms.all, 'list'] as const,
    list: (params?: Record<string, any>) => [...queryKeys.firms.lists(), params] as const,
    details: () => [...queryKeys.firms.all, 'detail'] as const,
    detail: (id: string | number) => [...queryKeys.firms.details(), id] as const,
  },

  // Add more feature query keys as needed
  // users: {
  //   all: ['users'] as const,
  //   lists: () => [...queryKeys.users.all, 'list'] as const,
  //   list: (params?: Record<string, any>) =>
  //     [...queryKeys.users.lists(), params] as const,
  //   details: () => [...queryKeys.users.all, 'detail'] as const,
  //   detail: (id: string | number) =>
  //     [...queryKeys.users.details(), id] as const,
  // },
} as const;

// Utility functions for cache management
export const cacheUtils = {
  // Invalidate all queries for a feature
  invalidateFeature: (feature: keyof typeof queryKeys) => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys[feature].all,
    });
  },

  // Remove all queries for a feature
  removeFeature: (feature: keyof typeof queryKeys) => {
    return queryClient.removeQueries({
      queryKey: queryKeys[feature].all,
    });
  },

  // Clear all cache
  clearAll: () => {
    return queryClient.clear();
  },

  // Get cached data
  getCachedData: <T>(queryKey: readonly unknown[]) => {
    return queryClient.getQueryData<T>(queryKey);
  },

  // Set cached data
  setCachedData: <T>(queryKey: readonly unknown[], data: T) => {
    return queryClient.setQueryData(queryKey, data);
  },
};
